/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import {
  dishCreatedModel,
  dishPayloadModel,
  dishPutPayloadModel,
  QUERY
} from '../api/DishesAPI.model'
import DishesAPI from '../api/DishesAPI'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

export function useDishesQuery(dishId?: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const { data: dishesList, isLoading: dishesListLoading } = useQuery({
    queryKey: [QUERY.DISHES],
    queryFn: DishesAPI.getDishes
  })

  const dish = useQuery({
    queryKey: [QUERY.DISHES, typeof dishId === 'string' ? dishId : ''],
    queryFn: () => DishesAPI.getDish(dishId!),
    enabled: typeof dishId === 'string' && dishId.length > 0
  })

  const dishImage = useQuery({
    queryKey: [
      QUERY.DISHES,
      typeof dishId === 'string' ? dishId : '',
      QUERY.DISH_IMAGE
    ],
    queryFn: () => DishesAPI.getDishImage(dishId!),
    enabled: typeof dishId === 'string' && dishId.length > 0
  })

  const { mutate: putDish } = useMutation<dishPutPayloadModel, ErrorModel, any>(
    {
      mutationFn: (dishPayload: dishPutPayloadModel) =>
        DishesAPI.putDish(dishPayload.dishPayload, dishPayload.dishId),
      onSuccess: () => {
        showSnackbar(
          t('ALERTS.PUT_DISH_SUCCESS.TITLE'),
          t('ALERTS.PUT_DISH_SUCCESS.DESCRIPTION'),
          'success'
        )
        queryClient.invalidateQueries({ queryKey: [QUERY.DISHES] })
      },
      onError: () => {
        showSnackbar(
          t('ALERTS.PUT_DISH_ERROR.TITLE'),
          t('ALERTS.PUT_DISH_ERROR.DESCRIPTION'),
          'error'
        )
      }
    }
  )

  // Async only: the page needs the created dish's id to attach its image.
  const { mutateAsync: postDishAsync } = useMutation<
    dishCreatedModel,
    ErrorModel,
    any
  >({
    mutationFn: (dishPayload: dishPayloadModel) =>
      DishesAPI.postDish(dishPayload),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_SUCCESS.TITLE'),
        t('ALERTS.POST_DISH_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.DISHES] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_ERROR.TITLE'),
        t('ALERTS.POST_DISH_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteDishes } = useMutation<void, ErrorModel, any>({
    mutationFn: (dishIds: string[]) => DishesAPI.deleteDishes(dishIds),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISHES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_DISHES_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.DISHES] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISHES_ERROR.TITLE'),
        t('ALERTS.DELETE_DISHES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  // Takes the target id per call rather than closing over the hook's `dishId`:
  // a dish gets its image right after being created, when the page still has
  // no id in state.
  const { mutateAsync: postDishImageAsync } = useMutation<
    void,
    ErrorModel,
    { file: File; dishId: string }
  >({
    mutationFn: ({ file, dishId: targetDishId }) => {
      const formData = new FormData()
      formData.append('file', file)
      return DishesAPI.postDishImage(formData, targetDishId)
    },
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_IMAGE_UPLOAD_SUCCESS.TITLE'),
        t('ALERTS.POST_DISH_IMAGE_UPLOAD_SUCCESS.DESCRIPTION'),
        'success'
      )
      // The whole DISHES prefix, not just this dish's image: the list
      // response carries the picture too, and the upload finishes after the
      // POST/PUT that refreshed the list, so a narrower key leaves the table
      // showing the old thumbnail until the page is reloaded.
      queryClient.invalidateQueries({ queryKey: [QUERY.DISHES] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_IMAGE_UPLOAD_ERROR.TITLE'),
        t('ALERTS.POST_DISH_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  // No variables: the target is the dish the hook was opened with.
  const { mutate: deleteDishImage } = useMutation<void, ErrorModel, void>({
    mutationFn: () => DishesAPI.deleteDishImage(dishId!),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISH_IMAGE_SUCCESS.TITLE'),
        t('ALERTS.DELETE_DISH_IMAGE_SUCCESS.DESCRIPTION'),
        'success'
      )
      // Same reasoning as the upload: the list carries the picture, so a
      // narrower key leaves the deleted thumbnail in the table. `remove`
      // rather than `invalidate` for the image query itself, the GET 404s
      // once the image is gone, and react-query keeps the last successful
      // data on a failed refetch, so the form would still show it.
      queryClient.removeQueries({
        queryKey: [QUERY.DISHES, dishId, QUERY.DISH_IMAGE]
      })
      queryClient.invalidateQueries({ queryKey: [QUERY.DISHES] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISH_IMAGE_ERROR.TITLE'),
        t('ALERTS.DELETE_DISH_IMAGE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    dishesList,
    dishesListLoading,
    dish,
    dishImage,
    putDish,
    postDishAsync,
    deleteDishes,
    postDishImageAsync,
    deleteDishImage
  }
}
